/**
 * Delivery history, Discord routing, and integrations.
 *
 * All three rendered prose and hardcoded literals before this. "Eligible for mentions" was the
 * string `1`, which would still say 1 after two more people linked Discord. A number that
 * cannot change is not a reading, and putting a "live" badge beside one is a lie the dashboard
 * tells confidently.
 *
 * The wording rule these views exist to hold: **Submitted is not Delivered, and neither is
 * Read.** Discord accepting a webhook POST is the last fact we have. Every label here stops
 * exactly there.
 */

import { Reading } from '../Reading';
import {
  BASE, getDelivery, getIntegrations, getRouting,
  type ApiFailure, type DeliveryRow, type IntegrationStatus, type RoutingPerson,
} from '../api';
import { usePoll } from '../usePoll';
import { central } from '../time';

/** A 401 is a signed-out browser, not an unreachable API: say so and offer the way back in. */
export function SignInNotice() {
  return (
    <div className="notice">
      <h3>Sign in to continue</h3>
      <p>Your session has ended.</p>
      <p style={{ marginTop: 10 }}><a href={`${BASE}/auth/login`}>Sign in with PeerTube</a></p>
    </div>
  );
}

export function Unavailable({ what, failure }: { what: string; failure: ApiFailure }) {
  if (failure.kind === 'unauthenticated') return <SignInNotice />;
  return (
    <div className="notice red">
      <h3>{what} cannot be read</h3>
      <p>{'reason' in failure ? failure.reason : 'The Operations API did not respond.'}</p>
    </div>
  );
}

export function Stale({ failure }: { failure: ApiFailure }) {
  return (
    <div className="notice red">
      <h3>No longer current</h3>
      <p>
        {'reason' in failure ? failure.reason : 'The last refresh failed.'} What follows is the
        last successful reading.
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ routing */

export function Routing() {
  const { data, failure, loading } = usePoll(getRouting);

  if (loading && !data) return <Reading label="Routing" state="unknown" source="loading" reason="Reading." />;
  if (!data) return <Unavailable what="Discord routing" failure={failure!} />;

  const unreachable = data.people.filter((p) => !p.reachable);

  return (
    <>
      {failure && <Stale failure={failure} />}

      <div className="readings">
        <Reading label="Channel" value={data.channel} state="live" source="worker configuration"
          observedAt={new Date(data.observedAt)} environment="production" />
        <Reading label="Can be mentioned" value={String(data.reachable)}
          state={failure ? 'stale' : 'live'} source="operations database"
          observedAt={new Date(data.observedAt)} environment="production"
          reason={`of ${data.people.length} authorised`} />
        <Reading label="Cannot be mentioned" value={String(unreachable.length)}
          state={unreachable.length > 0 ? 'error' : 'live'} source="operations database"
          observedAt={new Date(data.observedAt)} environment="production"
          reason={unreachable.length > 0 ? 'each one is listed below with the reason' : 'everyone authorised is reachable'} />
      </div>

      <div className="alert-list">
        {data.people.map((p) => <PersonRow key={p.peertubeUserId} person={p} />)}
      </div>

      <div className="notice blue">
        <h3>A channel message still reaches the channel</h3>
        <p>
          Someone who cannot be mentioned is not cut off. The alert still posts to {data.channel};
          what they lose is the notification that pulls them to it. Mentions are the difference
          between being told and having to look.
        </p>
      </div>
    </>
  );
}

function PersonRow({ person }: { person: RoutingPerson }) {
  return (
    <div className={`alert-row ${person.reachable ? '' : 'sev-warning'}`}>
      <div className="alert-head">
        <span className={`pill ${person.reachable ? 'ok' : 'sev-warning'}`}>
          {person.reachable ? 'reachable' : 'not reachable'}
        </span>
        <strong>{person.username ?? `PeerTube user ${person.peertubeUserId}`}</strong>
        {person.discordUsername && <span className="pill muted">{person.discordUsername}</span>}
      </div>
      {person.blockedBy && <p className="alert-summary">{person.blockedBy}</p>}
    </div>
  );
}

/* ------------------------------------------------------------- integrations */

export function Integrations() {
  const { data, failure, loading } = usePoll(getIntegrations, 30_000);

  if (loading && !data) return <Reading label="Integrations" state="unknown" source="loading" reason="Probing." />;
  if (!data) return <Unavailable what="Integration status" failure={failure!} />;

  return (
    <>
      {failure && <Stale failure={failure} />}
      <div className="alert-list">
        {data.integrations.map((i) => <IntegrationRow key={i.name} item={i} />)}
      </div>
      <div className="notice">
        <h3>Unknown is not down</h3>
        <p>
          A dependency marked unknown could not be reached from the Operations API. That is not
          the same as knowing it has failed, and it changes what to check first: the path, not
          the service.
        </p>
      </div>
    </>
  );
}

function IntegrationRow({ item }: { item: IntegrationStatus }) {
  const cls = item.state === 'down' ? 'sev-critical' : item.state === 'unknown' ? 'sev-warning' : '';
  return (
    <div className={`alert-row ${cls}`}>
      <div className="alert-head">
        <span className={`pill ${item.state === 'ok' ? 'ok' : item.state === 'down' ? 'sev-critical' : 'sev-warning'}`}>
          {item.state}
        </span>
        <strong>{item.name}</strong>
      </div>
      <p className="alert-summary">{item.detail}</p>
      <div className="alert-meta"><span>{item.purpose}</span></div>
    </div>
  );
}

/* ---------------------------------------------------------- delivery history */

export function DeliveryHistory() {
  const { data, failure, loading } = usePoll(getDelivery, 30_000);

  if (loading && !data) return <Reading label="Delivery history" state="unknown" source="loading" reason="Reading." />;
  if (!data) return <Unavailable what="Delivery history" failure={failure!} />;

  const rows = data.deliveries;
  const failed = rows.filter((r) => r.state === 'failed').length;
  const mentionedAnyone = rows.filter((r) => r.mentioned > 0).length;

  return (
    <>
      {failure && <Stale failure={failure} />}

      <div className="readings">
        <Reading label="Notifications" value={String(rows.length)} state={failure ? 'stale' : 'live'}
          source="operations database" observedAt={new Date()} environment="production"
          reason="most recent 50" />
        <Reading label="Mentioned someone" value={String(mentionedAnyone)}
          state={failure ? 'stale' : 'live'} source="operations database"
          observedAt={new Date()} environment="production"
          reason="the rest posted to the channel without a mention" />
        <Reading label="Failed" value={String(failed)}
          state={failed > 0 ? 'error' : 'live'} source="operations database"
          observedAt={new Date()} environment="production"
          reason={failed > 0 ? 'Discord did not accept these' : 'every attempt was accepted'} />
      </div>

      {rows.length === 0 ? (
        <div className="notice">
          <h3>Nothing has been sent</h3>
          <p>No notification has been queued yet. That is expected while no alert has fired.</p>
        </div>
      ) : (
        <div className="alert-list">{rows.map((r) => <DeliveryRowView key={r.id} row={r} />)}</div>
      )}

      <div className="notice blue">
        <h3>Submitted means Discord accepted it</h3>
        <p>
          It does not mean anyone read it, and it does not mean anyone acted. Read receipts do
          not exist on this path, so this view reports the last fact it actually has.
        </p>
      </div>
    </>
  );
}

function DeliveryRowView({ row }: { row: DeliveryRow }) {
  const cls = row.state === 'failed' ? 'sev-critical'
    : row.severity === 'critical' ? 'sev-critical'
    : row.severity === 'warning' ? 'sev-warning' : '';
  return (
    <div className={`alert-row ${cls}`}>
      <div className="alert-head">
        <span className={`pill ${row.state === 'failed' ? 'sev-critical' : 'ok'}`}>{row.state}</span>
        <strong>{row.summary}</strong>
        {row.severity && <span className={`pill sev-${row.severity}`}>{row.severity}</span>}
      </div>
      {row.lastError && <p className="alert-summary">{row.lastError}</p>}
      <div className="alert-meta">
        <span>{central(row.createdAt)}</span>
        <span>{row.mentioned > 0 ? `${row.mentioned} mentioned` : 'no mention'}</span>
        {row.attempts > 1 && <span>{row.attempts} attempts</span>}
        {row.lastAttempt?.httpStatus != null && <span>HTTP {row.lastAttempt.httpStatus}</span>}
      </div>
    </div>
  );
}
